// components/Compliance/MandatoryTrainingReport.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, FileDown, FileText, ClipboardList, CheckCircle2, Clock, CircleDashed, AlertTriangle, MinusCircle } from 'lucide-react';
import {
  PageHeader, AppButton, ConfirmModal, StatusBadge, StatsCard, StatsGrid,
} from '../ui';
import {
  getCampaignReport, exemptAssignment,
  downloadCampaignPdf, downloadCampaignCsv,
} from '../../utils/complianceApi';
import './_compliance.scss';

const STATUS_VARIANT = {
  completed: 'completed',
  in_progress: 'ongoing',
  pending: 'pending',
  overdue: 'destructive',
  exempted: 'inactive',
};

export default function MandatoryTrainingReport() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exemptTarget, setExemptTarget] = useState(null);
  const [reason, setReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getCampaignReport(id);
      setData(data || {});
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onExempt = async () => {
    if (!exemptTarget) return;
    await exemptAssignment(exemptTarget._id, reason || 'Exempted by admin');
    setExemptTarget(null);
    setReason('');
    load();
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const onDownloadPdf = async () => {
    const r = await downloadCampaignPdf(id);
    const safeName = (data?.campaign?.name || 'campaign').replace(/[^a-z0-9_-]+/gi, '_');
    triggerDownload(r.data, `compliance-${safeName}.pdf`);
  };
  const onDownloadCsv = async () => {
    const r = await downloadCampaignCsv(id);
    const safeName = (data?.campaign?.name || 'campaign').replace(/[^a-z0-9_-]+/gi, '_');
    triggerDownload(r.data, `compliance-${safeName}.csv`);
  };

  if (loading) return <div className="cp-page">Loading…</div>;
  if (!data) return <div className="cp-page">Report unavailable.</div>;

  const { campaign = {}, summary = {}, assignments = [] } = data;
  const total = summary.total || assignments.length || 0;
  const completionPct = total ? Math.round(((summary.completed || 0) / total) * 100) : 0;

  return (
    <div className="cp-page">
      <PageHeader
        title={`Report — ${campaign.name || 'Campaign'}`}
        icon={ShieldCheck}
        backButton
        onBack={() => navigate('/compliance/mandatory-training')}
        actions={
          <>
            <AppButton variant="outline" onClick={onDownloadCsv}>
              <FileText size={16} /> Export CSV
            </AppButton>
            <AppButton variant="default" onClick={onDownloadPdf}>
              <FileDown size={16} /> Export PDF
            </AppButton>
          </>
        }
      />

      <StatsGrid cols={6} className="cp-stats">
        <StatsCard title="Total Assigned" value={total} icon={ClipboardList} iconColor="blue" />
        <StatsCard title="Completed" value={summary.completed || 0} icon={CheckCircle2} iconColor="emerald" />
        <StatsCard title="In Progress" value={summary.in_progress || 0} icon={Clock} iconColor="cyan" />
        <StatsCard title="Pending" value={summary.pending || 0} icon={CircleDashed} iconColor="amber" />
        <StatsCard title="Overdue" value={summary.overdue || 0} icon={AlertTriangle} iconColor="rose" />
        <StatsCard title="Exempted" value={summary.exempted || 0} icon={MinusCircle} iconColor="violet" />
      </StatsGrid>

      <div className="cp-stat">
        <div className="cp-stat__label">Completion</div>
        <div className="cp-progress">
          <div className="cp-progress__bar">
            <div className="cp-progress__fill" style={{ width: `${completionPct}%` }} />
          </div>
          <span>{completionPct}%</span>
        </div>
      </div>

      <div className="cp-table-wrap">
        {!assignments.length ? (
          <div className="cp-empty">No assignments yet.</div>
        ) : (
          <table className="cp-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Email</th>
                <th>Department</th>
                <th>Status</th>
                <th>Completed</th>
                <th>Score</th>
                <th className="cp-col--shrink"></th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a._id}>
                  <td>{a.student?.name || '—'}</td>
                  <td>{a.student?.email || '—'}</td>
                  <td>{a.student?.department || '—'}</td>
                  <td><StatusBadge status={STATUS_VARIANT[a.status] || 'pending'}>{a.status}</StatusBadge></td>
                  <td>{a.completedAt ? new Date(a.completedAt).toLocaleDateString() : '—'}</td>
                  <td>{Number.isFinite(a.score) ? `${Math.round(a.score)}%` : '—'}</td>
                  <td className="cp-col--shrink">
                    {a.status !== 'completed' && a.status !== 'exempted' && (
                      <AppButton variant="ghost" size="sm" onClick={() => setExemptTarget(a)} title="Exempt">
                        <ShieldAlert size={14} /> Exempt
                      </AppButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmModal
        show={!!exemptTarget}
        title="Exempt this employee?"
        message={
          <div>
            <p>Mark this assignment as exempted. Provide a reason for the audit log:</p>
            <textarea
              className="cp-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. on extended leave, role no longer applicable"
            />
          </div>
        }
        confirmText="Exempt"
        variant="warning"
        onConfirm={onExempt}
        onHide={() => { setExemptTarget(null); setReason(''); }}
      />
    </div>
  );
}
